import React, { useEffect, useState, useMemo } from 'react';
import './InventoryDashboard.css';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Table,
  Button,
  Input,
  Space,
  Tag,
  Card,
  Select,
  Row,
  Col,
  Progress,
  Typography,
  Collapse,
  Spin,
} from 'antd';
import {
  SearchOutlined,
  WarningOutlined,
  BarChartOutlined,
  ProfileOutlined,
  DownOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { getApiErrorMessage } from '../utils/apiError';
import { useFeedback } from '../utils/feedback';
import { authService } from '../services/auth';
import { formatDate } from '../utils/dateUtils';
import { formatCurrency } from '../utils/currency';
import { Inventory, InventoryBatch, InventoryMovement } from '../types/pz';
import { canManageInventory } from '../modules/inventory/inventoryAccessPolicy';
import { fetchBatchEarmarks, BatchEarmark } from '../modules/orders/api/orderEarmarksApi';
import { usePageAutoRefresh } from '../hooks/usePageAutoRefresh';
import { fetchInventoryList, fetchInventoryVariantDetail } from '../modules/inventory/api/inventoryApi';
import { getInventoryStockStatus } from '../dto/inventory';
import {
  formatQuantitySet,
  hasPersistedQuantitySet,
  quantitySetFromFields,
  quantitySetRatio,
} from '../utils/commercialQuantity';

const { Option } = Select;
const { Text } = Typography;

const EMPTY_QTY = '—';

type InventoryWithDetails = Inventory;

interface InventoryFilters {
  search: string;
  status: string | null;
  lowStock: boolean;
}

interface InventorySummary {
  totalItems: number;
  totalValue: number;
  lowStockCount: number;
  outOfStockCount: number;
}

interface InventoryCategoryGroup {
  key: string;
  name: string;
  color: string | null;
  rows: InventoryWithDetails[];
  inStockCount: number;
  outOfStockCount: number;
  inTransitCount: number;
}

interface InventoryVariantDetailState {
  loading: boolean;
  error: string | null;
  batches: InventoryBatch[];
  movements: InventoryMovement[];
}

const DEFAULT_FILTERS: InventoryFilters = {
  search: '',
  status: null,
  lowStock: false,
};

type StockStatus = 'out' | 'low' | 'in';

const hasPositiveQuantitySet = (record: object | null | undefined, prefix: string) => (
  Object.values(quantitySetFromFields(record as Record<string, unknown> | undefined, prefix))
    .some((value) => value !== null && value > 0)
);

const deriveStockStatus = (inv: InventoryWithDetails): StockStatus => {
  const status = getInventoryStockStatus(inv);
  if (status === 'OUT_OF_STOCK') return 'out';
  if (status === 'LOW_STOCK') return 'low';
  return 'in';
};

// UI-side tokens kept on the Select (and their existing i18n labels) map onto
// the derived status above rather than the unreliable persisted column.
const STATUS_FILTER_TO_DERIVED: Record<string, StockStatus> = {
  ACTIVE: 'in',
  LOW_STOCK: 'low',
  OUT_OF_STOCK: 'out',
};

const InventoryDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation(['inventory', 'common']);
  const feedback = useFeedback();

  const [inventory, setInventory] = useState<InventoryWithDetails[]>([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState<InventoryFilters>(DEFAULT_FILTERS);
  const [summary, setSummary] = useState<InventorySummary>({
    totalItems: 0,
    totalValue: 0,
    lowStockCount: 0,
    outOfStockCount: 0,
  });
  const [expandedRowKeys, setExpandedRowKeys] = useState<string[]>([]);
  const [variantDetails, setVariantDetails] = useState<Record<string, InventoryVariantDetailState>>({});
  // batchEarmarks: keyed by batchId (batch.id), loaded on demand per batch row
  const [batchEarmarks, setBatchEarmarks] = useState<Record<string, BatchEarmark[]>>({});

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const canEdit = canManageInventory(authService.getCurrentUser());
  const canViewPurchasePrices = authService.canViewSupplierPurchasePrices();
  const canViewBatchProfitability = authService.canViewFinancialReports();

  useEffect(() => {
    fetchInventory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchInventory = async () => {
    setLoading(true);
    try {
      const inventoryData = await fetchInventoryList();
      setInventory(inventoryData);

      // Calculate summary
      const totalValue = canViewPurchasePrices
        ? inventoryData.reduce((sum: number, inv: InventoryWithDetails) => sum + (inv.totalValuePLN || 0), 0)
        : 0;
      const lowStockCount = inventoryData.filter((inv: InventoryWithDetails) =>
        deriveStockStatus(inv) === 'low').length;
      const outOfStockCount = inventoryData.filter((inv: InventoryWithDetails) =>
        deriveStockStatus(inv) === 'out').length;

      setSummary({
        totalItems: inventoryData.length,
        totalValue,
        lowStockCount,
        outOfStockCount,
      });

      // Returning to an already-expanded row must refresh the same atomic
      // detail read model as the aggregate. Otherwise a PZ/WZ completed in a
      // second tab updates the headline while leaving old batch history open.
      await Promise.all(expandedRowKeys.map((variantId) => fetchVariantDetails(variantId)));
    } catch (error: any) {
      feedback.error(getApiErrorMessage(error, t, 'inventory:error.fetchFailed'));
    } finally {
      setLoading(false);
    }
  };

  usePageAutoRefresh(fetchInventory);

  const fetchVariantDetails = async (variantId: string) => {
    setVariantDetails(prev => ({
      ...prev,
      [variantId]: {
        loading: true,
        error: null,
        batches: prev[variantId]?.batches || [],
        movements: prev[variantId]?.movements || [],
      },
    }));

    try {
      const detail = await fetchInventoryVariantDetail(variantId);
      setVariantDetails(prev => ({
        ...prev,
        [variantId]: {
          loading: false,
          error: null,
          batches: detail.batches,
          movements: detail.recentMovements,
        },
      }));
    } catch (error: any) {
      console.error('Error fetching inventory variant detail:', error);
      setVariantDetails(prev => ({
        ...prev,
        [variantId]: {
          loading: false,
          error: getApiErrorMessage(error, t, 'inventory:error.detailsFetchFailed'),
          batches: [],
          movements: [],
        },
      }));
    }
  };

  const fetchEarmarksForBatch = async (batchId: string) => {
    if (batchEarmarks[batchId] !== undefined) return; // already loaded
    try {
      const earmarks = await fetchBatchEarmarks(batchId);
      setBatchEarmarks(prev => ({ ...prev, [batchId]: earmarks }));
    } catch (error: any) {
      console.error('Error fetching batch earmarks:', error);
      setBatchEarmarks(prev => ({ ...prev, [batchId]: [] }));
    }
  };

  const handleExpand = (expanded: boolean, record: InventoryWithDetails) => {
    const variantId = record.productVariantId;
    if (expanded) {
      setExpandedRowKeys(current => current.includes(variantId) ? current : [...current, variantId]);
      if (!variantDetails[variantId] || variantDetails[variantId].error) {
        void fetchVariantDetails(variantId);
      }
    } else {
      setExpandedRowKeys(current => current.filter(key => key !== variantId));
    }
  };

  const filteredInventory = useMemo(() => {
    return inventory.filter(inv => {
      // Search filter
      if (filters.search) {
        const searchLower = filters.search.toLowerCase();
        const matchesVariant =
          inv.productVariant?.sku.toLowerCase().includes(searchLower) ||
          inv.productVariant?.variantName.toLowerCase().includes(searchLower);
        const matchesProduct =
          inv.productVariant?.product?.code.toLowerCase().includes(searchLower) ||
          inv.productVariant?.product?.name.toLowerCase().includes(searchLower);
        if (!matchesVariant && !matchesProduct) return false;
      }

      // Status filter — derived from quantities, not the unreliable persisted
      // Inventory.status column (see deriveStockStatus above).
      if (filters.status) {
        const targetStatus = STATUS_FILTER_TO_DERIVED[filters.status];
        if (targetStatus && deriveStockStatus(inv) !== targetStatus) return false;
      }

      // Low stock filter — same derivation, so it always agrees with the
      // "Niski stan" KPI count and the status Select's LOW_STOCK option.
      if (filters.lowStock && deriveStockStatus(inv) !== 'low') return false;

      return true;
    });
  }, [inventory, filters]);

  const groupedInventory = useMemo<InventoryCategoryGroup[]>(() => {
    const groups = new Map<string, InventoryCategoryGroup>();

    filteredInventory.forEach((row) => {
      const product = row.productVariant?.product;
      const key = product?.productCategoryId || 'uncategorized';
      const group = groups.get(key) || {
        key,
        name: product?.productCategoryName || t('inventory:categories.uncategorized'),
        color: product?.productCategoryColor || null,
        rows: [],
        inStockCount: 0,
        outOfStockCount: 0,
        inTransitCount: 0,
      };

      group.rows.push(row);
      if (deriveStockStatus(row) === 'in') group.inStockCount += 1;
      if (deriveStockStatus(row) === 'out') group.outOfStockCount += 1;
      if (
        hasPersistedQuantitySet(row as unknown as Record<string, unknown>, 'quantityInTransit')
          ? hasPositiveQuantitySet(row, 'quantityInTransit')
          : Number(row.quantityInTransit || 0) > 0
      ) group.inTransitCount += 1;
      groups.set(key, group);
    });

    return Array.from(groups.values())
      .map(group => ({
        ...group,
        rows: [...group.rows].sort((a, b) => {
          const productCompare = (a.productVariant?.product?.name || '').localeCompare(
            b.productVariant?.product?.name || '', i18n.language,
          );
          if (productCompare !== 0) return productCompare;
          return (a.productVariant?.variantName || '').localeCompare(
            b.productVariant?.variantName || '', i18n.language,
          );
        }),
      }))
      .sort((a, b) => {
        if (a.key === 'uncategorized') return 1;
        if (b.key === 'uncategorized') return -1;
        return a.name.localeCompare(b.name, i18n.language);
      });
  }, [filteredInventory, i18n.language, t]);

  const hasActiveFilters = Boolean(filters.search || filters.status || filters.lowStock);

  const clearFilters = () => setFilters(DEFAULT_FILTERS);

  const toggleLowStockFilter = () => {
    setFilters(prev => ({ ...prev, lowStock: !prev.lowStock }));
  };

  const toggleOutOfStockFilter = () => {
    setFilters(prev => ({ ...prev, status: prev.status === 'OUT_OF_STOCK' ? null : 'OUT_OF_STOCK' }));
  };

  const getMovementTypeTag = (type: string) => {
    switch (type) {
      case 'PZ_IN':
        return <Tag color="green">PZ</Tag>;
      case 'WZ_OUT':
        return <Tag color="red">WZ</Tag>;
      case 'ADJUSTMENT':
      case 'ADJUSTMENT_IN':
      case 'ADJUSTMENT_OUT':
        return <Tag color="blue">{t('inventory:movementType.adjustment')}</Tag>;
      case 'TRANSFER':
        return <Tag color="purple">{t('inventory:movementType.transfer')}</Tag>;
      default:
        return <Tag>{type}</Tag>;
    }
  };

  const renderQuantitySetText = (
    record: object,
    prefix: string,
    legacyQuantity: number,
    absolute = false,
  ) => {
    return formatQuantitySet(
      record as Record<string, unknown>,
      prefix,
      i18n.language,
      {
        absolute,
        fallbackQuantity: legacyQuantity,
        fallbackUnit: (record as { quantityUnit?: Inventory['quantityUnit'] }).quantityUnit,
      },
    );
  };

  const renderAvailable = (qty: number, record: InventoryWithDetails) => {
    const status = deriveStockStatus(record);
    const tone = status === 'in' ? 'ok' : status;

    return (
      <span className={`inventory-dashboard__qty inventory-dashboard__qty--${tone}`}>
        <span className="inventory-dashboard__dot" aria-hidden="true" />
        {renderQuantitySetText(record, 'quantityAvailable', qty)}
      </span>
    );
  };

  // Zeros are noise on these two columns — a CFO scanning the table wants
  // exceptions to pop, not a wall of "0".
  const renderQuantity = (
    qty: number,
    record: InventoryWithDetails,
    prefix: 'quantityReserved' | 'quantityInTransit',
  ) => {
    const value = qty || 0;
    const text = renderQuantitySetText(record, prefix, value);
    const hasCanonicalValue = ['Pcs', 'M2', 'M3'].some((suffix) => (
      (record as unknown as Record<string, number | null | undefined>)[`${prefix}${suffix}`] != null
    ));
    if (!value && !hasCanonicalValue) {
      return <span className="inventory-dashboard__qty inventory-dashboard__qty--muted">{EMPTY_QTY}</span>;
    }
    return <span className="inventory-dashboard__qty">{text}</span>;
  };

  const columns = [
    {
      title: t('inventory:fields.productName'),
      key: 'productName',
      render: (record: InventoryWithDetails) => (
        <div className="inventory-dashboard__product">
          <div className="inventory-dashboard__product-name">{record.productVariant?.product?.name}</div>
          <div className="inventory-dashboard__product-meta">{record.productVariant?.variantName}</div>
        </div>
      ),
    },
    {
      title: t('inventory:fields.status'),
      key: 'stockStatus',
      width: 130,
      render: (record: InventoryWithDetails) => {
        const status = deriveStockStatus(record);
        const labels: Record<StockStatus, string> = {
          in: t('inventory:status.inStock'),
          low: t('inventory:status.lowStock'),
          out: t('inventory:status.outOfStock'),
        };
        return <span className={`inventory-dashboard__status inventory-dashboard__status--${status}`}>{labels[status]}</span>;
      },
    },
    {
      title: t('inventory:fields.quantityAvailable'),
      dataIndex: 'quantityAvailable',
      key: 'quantityAvailable',
      width: 150,
      align: 'right' as const,
      render: (qty: number, record: InventoryWithDetails) => renderAvailable(qty, record),
    },
    {
      title: t('inventory:fields.quantityReserved'),
      dataIndex: 'quantityReserved',
      key: 'quantityReserved',
      width: 150,
      align: 'right' as const,
      render: (qty: number, record: InventoryWithDetails) => renderQuantity(qty, record, 'quantityReserved'),
    },
    {
      title: t('inventory:fields.quantityInTransit'),
      dataIndex: 'quantityInTransit',
      key: 'quantityInTransit',
      width: 150,
      align: 'right' as const,
      render: (qty: number, record: InventoryWithDetails) => renderQuantity(qty, record, 'quantityInTransit'),
    },
  ];

  // Expanded row to show batches and movements
  const expandedRowRender = (record: InventoryWithDetails) => {
    const variantId = record.productVariantId;
    const detail = variantDetails[variantId];

    if (!detail || detail.loading) {
      return (
        <div style={{ padding: 32, textAlign: 'center' }}>
          <Space direction="vertical">
            <Spin />
            <Text type="secondary">{t('inventory:details.loading')}</Text>
          </Space>
        </div>
      );
    }

    if (detail.error) {
      return (
        <Alert
          type="error"
          showIcon
          message={t('inventory:details.errorTitle')}
          description={detail.error}
          action={(
            <Button size="small" onClick={() => void fetchVariantDetails(variantId)}>
              {t('common:retry')}
            </Button>
          )}
        />
      );
    }

    const variantBatches = detail.batches;
    const variantMovements = detail.movements;

    const batchColumns = [
      {
        title: t('inventory:batchFields.sourceDocument'),
        key: 'batchNumber',
        render: (_: unknown, batch: InventoryBatch) => (
          batch.pzDocument?.pzNumber || batch.batchNumber
        ),
      },
      {
        title: t('inventory:batchFields.receiptDate'),
        dataIndex: 'receiptDate',
        key: 'receiptDate',
        render: (date: string) => formatDate(date),
      },
      {
        title: t('inventory:batchFields.quantityReceived'),
        dataIndex: 'quantityReceived',
        key: 'quantityReceived',
        render: (qty: number, batch: InventoryBatch) => (
          renderQuantitySetText(batch, 'quantityReceived', qty)
        ),
      },
      {
        title: t('inventory:batchFields.quantityRemaining'),
        dataIndex: 'quantityRemaining',
        key: 'quantityRemaining',
        render: (qty: number, batch: InventoryBatch) => {
          const setRatio = quantitySetRatio(
            quantitySetFromFields(batch as unknown as Record<string, unknown>, 'quantityRemaining'),
            quantitySetFromFields(batch as unknown as Record<string, unknown>, 'quantityReceived'),
          ).ratio;
          const legacyRatio = batch.quantityReceived > 0 ? qty / batch.quantityReceived : 0;
          const percent = Math.max(0, Math.min(100, Math.round((setRatio ?? legacyRatio) * 100)));
          return (
            <Progress
              percent={percent}
              size="small"
              format={() => renderQuantitySetText(batch, 'quantityRemaining', qty)}
            />
          );
        },
      },
      ...(canViewPurchasePrices ? [{
        title: t('inventory:batchFields.unitCost'),
        dataIndex: 'unitCostPLN',
        key: 'unitCostPLN',
        render: (cost: number) => formatCurrency(cost, 'PLN'),
      }] : []),
      {
        title: t('inventory:batchFields.status'),
        dataIndex: 'status',
        key: 'status',
        render: (status: string) => (
          <Tag color={status === 'ACTIVE' ? 'green' : status === 'DEPLETED' ? 'default' : 'red'}>
            {t(`inventory:batchStatus.${status.toLowerCase()}`)}
          </Tag>
        ),
      },
    ];

    const movementColumns = [
      {
        title: t('inventory:movementFields.date'),
        dataIndex: 'movementDate',
        key: 'movementDate',
        render: (date: string) => formatDate(date),
      },
      {
        title: t('inventory:movementFields.type'),
        dataIndex: 'movementType',
        key: 'movementType',
        render: (type: string) => getMovementTypeTag(type),
      },
      {
        title: t('inventory:movementFields.quantity'),
        dataIndex: 'quantity',
        key: 'quantity',
        render: (qty: number, mov: InventoryMovement) => {
          const inbound = mov.movementType.includes('IN') || mov.movementType === 'RETURN';
          return (
            <Text type={inbound ? 'success' : 'danger'}>
              {inbound ? '+' : '-'}{renderQuantitySetText(mov, 'quantity', qty, true)}
            </Text>
          );
        },
      },
      {
        title: t('inventory:movementFields.reference'),
        dataIndex: 'referenceNumber',
        key: 'referenceNumber',
        render: (ref: string) => ref || '-',
      },
      {
        title: t('inventory:movementFields.notes'),
        dataIndex: 'notes',
        key: 'notes',
        render: (notes: string) => notes || '-',
      },
    ];

    // Earmark columns for the per-batch "reserved for orders" sub-table.
    // Uses inventory:earmarks.* keys that were pre-provisioned in Ticket C.
    const earmarkColumns = [
      {
        title: t('inventory:earmarks.colOrder'),
        dataIndex: 'orderNumber',
        key: 'orderNumber',
      },
      {
        title: t('inventory:earmarks.colQty'),
        dataIndex: 'quantity',
        key: 'quantity',
        render: (qty: number, earmark: BatchEarmark) => (
          renderQuantitySetText(earmark, 'quantity', qty)
        ),
      },
      {
        title: t('inventory:earmarks.colConsumed'),
        dataIndex: 'consumedQuantity',
        key: 'consumedQuantity',
        render: (qty: number, earmark: BatchEarmark) => (
          renderQuantitySetText(earmark, 'consumedQuantity', qty)
        ),
      },
      {
        title: t('inventory:earmarks.colStatus'),
        dataIndex: 'status',
        key: 'status',
        render: (status: string) => (
          <Tag color={status === 'ACTIVE' ? 'blue' : status === 'FULFILLED' ? 'green' : 'default'}>
            {t(`inventory:earmarks.status_${status}`)}
          </Tag>
        ),
      },
      {
        title: t('inventory:earmarks.colEarmarkedAt'),
        dataIndex: 'earmarkedAt',
        key: 'earmarkedAt',
        render: (date: string) => formatDate(date),
      },
    ];

    // Expanded sub-row for a single batch — shows active earmarks (orders that
    // reserved units from this batch). Loads on first expand; subsequent expands
    // use the cached result from batchEarmarks state.
    const batchExpandedRowRender = (batch: InventoryBatch) => {
      const earmarks = batchEarmarks[batch.id];
      // Filter to ACTIVE earmarks only — fulfilled/released are noise in this view
      const activeEarmarks = earmarks
        ? earmarks.filter((e: BatchEarmark) => e.status === 'ACTIVE')
        : undefined;

      return (
        <div className="inventory-dashboard__earmarks">
          <div className="inventory-dashboard__earmarks-title">
            {t('inventory:earmarks.title')}
          </div>
          <Table
            columns={earmarkColumns}
            dataSource={activeEarmarks}
            loading={earmarks === undefined}
            pagination={false}
            rowKey="id"
            size="small"
            locale={{ emptyText: t('inventory:earmarks.noData') }}
          />
        </div>
      );
    };

    return (
      <div className="inventory-dashboard__expanded">
        <Row gutter={24}>
          <Col xs={24} xl={12}>
            <Card title={t('inventory:sections.batches')} size="small">
              <Table
                columns={batchColumns}
                dataSource={variantBatches}
                pagination={false}
                rowKey="id"
                size="small"
                locale={{ emptyText: t('inventory:noBatches') }}
                scroll={{ x: 620 }}
                expandable={{
                  expandedRowRender: batchExpandedRowRender,
                  onExpand: (expanded: boolean, batch: InventoryBatch) => {
                    if (expanded) {
                      fetchEarmarksForBatch(batch.id);
                    }
                  },
                }}
              />
            </Card>
          </Col>
          <Col xs={24} xl={12}>
            <Card title={t('inventory:sections.recentMovements')} size="small">
              <Table
                columns={movementColumns}
                dataSource={variantMovements}
                pagination={false}
                rowKey="id"
                size="small"
                locale={{ emptyText: t('inventory:noMovements') }}
                scroll={{ x: 620 }}
              />
            </Card>
          </Col>
        </Row>
      </div>
    );
  };

  return (
    <div className="inventory-dashboard-page">
      {/* No <h1> here: Layout.tsx already renders "Magazyn" in the sticky
          header via menu.inventory. This row only carries the report links. */}
      <div className="inventory-dashboard__masthead">
        <Space>
          {/* Both batch reports sit behind the same financial-reports gate. */}
          {canViewBatchProfitability && (
            <>
              <Button
                icon={<ProfileOutlined />}
                onClick={() => navigate('/app/inventory/batch-ledger')}
              >
                {t('inventory:actions.viewBatchLedger')}
              </Button>
              <Button
                icon={<BarChartOutlined />}
                onClick={() => navigate('/app/inventory/batch-profitability')}
              >
                {t('inventory:actions.viewBatchProfitability')}
              </Button>
            </>
          )}
          {canViewPurchasePrices && (
            <Button
              icon={<BarChartOutlined />}
              onClick={() => navigate('/app/inventory/reports')}
            >
              {t('inventory:actions.viewReports')}
            </Button>
          )}
        </Space>
      </div>

      {/* KPI band — quiet stat strip, in the same slate visual language as
          BatchLedger's totals strip. The low-stock and out-of-stock cells are
          interactive: they double as the low-stock/status filter toggles, so
          the old duplicate yellow Alert banner is gone. */}
      <div className="inventory-dashboard__kpis">
        <div className="inventory-dashboard__kpi">
          <span className="inventory-dashboard__kpi-label">{t('inventory:summary.totalItems')}</span>
          <span className="inventory-dashboard__kpi-value">{summary.totalItems}</span>
        </div>
        {canViewPurchasePrices && (
          <div className="inventory-dashboard__kpi">
            <span className="inventory-dashboard__kpi-label">{t('inventory:summary.totalValue')}</span>
            <span className="inventory-dashboard__kpi-value">{formatCurrency(summary.totalValue, 'PLN')}</span>
          </div>
        )}
        <button
          type="button"
          className={`inventory-dashboard__kpi inventory-dashboard__kpi--interactive inventory-dashboard__kpi--warning${summary.lowStockCount > 0 ? ' has-value' : ''}${filters.lowStock ? ' is-active' : ''}`}
          aria-pressed={filters.lowStock}
          onClick={toggleLowStockFilter}
        >
          <span className="inventory-dashboard__kpi-label">{t('inventory:summary.lowStock')}</span>
          <span className="inventory-dashboard__kpi-value">{summary.lowStockCount}</span>
        </button>
        <button
          type="button"
          className={`inventory-dashboard__kpi inventory-dashboard__kpi--interactive inventory-dashboard__kpi--danger${summary.outOfStockCount > 0 ? ' has-value' : ''}${filters.status === 'OUT_OF_STOCK' ? ' is-active' : ''}`}
          aria-pressed={filters.status === 'OUT_OF_STOCK'}
          onClick={toggleOutOfStockFilter}
        >
          <span className="inventory-dashboard__kpi-label">{t('inventory:summary.outOfStock')}</span>
          <span className="inventory-dashboard__kpi-value">{summary.outOfStockCount}</span>
        </button>
      </div>

      {/* Flat toolbar — frontend-pages.md / Products.tsx reference pattern. */}
      <div className="inventory-dashboard__toolbar">
        <Input
          className="inventory-dashboard__search"
          placeholder={t('inventory:filters.search')}
          prefix={<SearchOutlined />}
          value={filters.search}
          onChange={(e) => setFilters({ ...filters, search: e.target.value })}
          allowClear
        />
        <Space wrap>
          <Select
            placeholder={t('inventory:filters.status')}
            value={filters.status}
            onChange={(value) => setFilters({ ...filters, status: value })}
            allowClear
            style={{ width: 190 }}
          >
            <Option value="ACTIVE">{t('inventory:status.inStock')}</Option>
            <Option value="LOW_STOCK">{t('inventory:status.lowStock')}</Option>
            <Option value="OUT_OF_STOCK">{t('inventory:status.outOfStock')}</Option>
          </Select>
          <Button
            type={filters.lowStock ? 'primary' : 'default'}
            icon={<WarningOutlined />}
            onClick={toggleLowStockFilter}
          >
            {t('inventory:filters.lowStockOnly')}
          </Button>
          {hasActiveFilters && (
            <Button type="link" onClick={clearFilters}>
              {t('common:clearFilters')}
            </Button>
          )}
        </Space>
      </div>

      <div className="inventory-dashboard__groups" aria-busy={loading}>
        {groupedInventory.map((group) => (
          <Collapse
            key={group.key}
            className="inventory-dashboard__category"
            bordered={false}
            defaultActiveKey={['inventory']}
            expandIconPosition="end"
            expandIcon={({ isActive }) => <DownOutlined rotate={isActive ? 180 : 0} />}
            items={[{
              key: 'inventory',
              label: (
                <div className="inventory-dashboard__category-header">
                  <div className="inventory-dashboard__category-identity">
                    <span
                      className="inventory-dashboard__category-mark"
                      style={{ backgroundColor: group.color || undefined }}
                      aria-hidden="true"
                    />
                    <span className="inventory-dashboard__category-name">{group.name}</span>
                    <span className="inventory-dashboard__category-count">
                      {t('inventory:categories.itemCount', { count: group.rows.length })}
                    </span>
                  </div>
                  <div className="inventory-dashboard__category-totals" aria-hidden="true">
                    <span><b>{group.inStockCount}</b>{t('inventory:categories.inStock')}</span>
                    <span><b>{group.outOfStockCount}</b>{t('inventory:categories.outOfStock')}</span>
                    <span><b>{group.inTransitCount}</b>{t('inventory:categories.inTransit')}</span>
                  </div>
                </div>
              ),
              children: (
                <Table
                  className="inventory-dashboard__table"
                  dataSource={group.rows}
                  columns={columns}
                  loading={loading}
                  rowKey="productVariantId"
                  scroll={{ x: 920 }}
                  expandable={{
                    expandedRowRender,
                    expandedRowKeys,
                    onExpand: handleExpand,
                  }}
                  pagination={false}
                />
              ),
            }]}
          />
        ))}
        {!loading && groupedInventory.length === 0 && (
          <div className="inventory-dashboard__empty">{t('inventory:categories.noMatches')}</div>
        )}
      </div>
    </div>
  );
};

export default InventoryDashboard;
